import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import VideoCard from "@/components/base/VideoCard";
import { categories, gossipItems, siteIntro } from "@/mocks/gossip";
import { formatDate, formatViews, getCategoryName } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function VideoPage() {
  const { id = "" } = useParams();
  const item = gossipItems.find((v) => v.id === id);
  const category = categories.find((c) => c.slug === item?.category);

  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  const related = useMemo(() => {
    if (!item) return [];
    const sameCategory = gossipItems.filter((v) => v.category === item.category && v.id !== item.id);
    const others = gossipItems.filter((v) => v.category !== item.category && v.id !== item.id);
    return [...sameCategory, ...others].slice(0, 4);
  }, [item]);

  const hotList = useMemo(() => [...gossipItems].sort((a, b) => b.views - a.views).slice(0, 6), []);

  usePageMeta({
    title: item ? `${item.title} - 视频吃瓜${getCategoryName(item.category, categories)}` : "内容不存在 | 瓜田影像社",
    description: item
      ? `${item.excerpt} 视频吃瓜${getCategoryName(item.category, categories)}内容，时长 ${item.duration}，${formatDate(item.date)}发布。`
      : "该内容暂不存在，请返回首页浏览更多视频吃瓜现场画面。",
    keywords: item ? `视频吃瓜,${item.tags.join(",")},${getCategoryName(item.category, categories)}` : "视频吃瓜",
    path: `/video/${id}`,
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: item.title,
            description: item.excerpt,
            thumbnailUrl: item.cover,
            uploadDate: item.date,
            duration: `PT${item.duration.replace(":", "M")}S`,
            contentUrl: `https://guatian-live.example.com/video/${item.id}`,
            inLanguage: "zh-CN",
            publisher: {
              "@type": "Organization",
              name: siteIntro.brandName,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://guatian-live.example.com/" },
              category
                ? {
                    "@type": "ListItem",
                    position: 2,
                    name: category.name,
                    item: `https://guatian-live.example.com/category/${category.slug}`,
                  }
                : undefined,
              { "@type": "ListItem", position: 3, name: item.title },
            ].filter(Boolean),
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-600">
            <i className="ri-film-line w-7 h-7 flex items-center justify-center"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这段现场内容不存在</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">内容可能已下线或链接有误，返回首页看看今天的热榜吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const categoryName = getCategoryName(item.category, categories);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
              { label: "视频详情" },
            ]}
          />

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="relative w-full overflow-hidden rounded-lg bg-foreground-950">
                <div className="relative w-full aspect-video">
                  <img
                    src={item.cover}
                    alt={`${item.title} 视频吃瓜${categoryName}画面`}
                    title={`${item.title} 视频吃瓜 ${categoryName}`}
                    className={`w-full h-full object-cover object-top transition-all duration-500 ${playing ? "scale-105 opacity-80" : ""}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>

                  {!playing ? (
                    <button
                      type="button"
                      onClick={() => setPlaying(true)}
                      aria-label={`播放 ${item.title}`}
                      className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center"
                    >
                      <span className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-background-50/95 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                        <i className="ri-play-fill w-8 h-8 md:w-9 md:h-9 flex items-center justify-center"></i>
                      </span>
                      <span className="mt-4 text-sm text-background-50">点击播放现场原声</span>
                    </button>
                  ) : (
                    <div className="absolute bottom-0 left-0 w-full px-4 pb-5">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setPlaying(false)}
                          aria-label="暂停播放"
                          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-background-50/90 text-foreground-950 cursor-pointer transition-colors hover:bg-background-50"
                        >
                          <i className="ri-pause-fill w-5 h-5 flex items-center justify-center"></i>
                        </button>
                        <div className="h-1 flex-1 overflow-hidden rounded-full bg-background-50/30">
                          <div className="h-full bg-accent-500 animate-progress-bar"></div>
                        </div>
                        <span className="text-xs text-background-50 font-label">{item.duration}</span>
                      </div>
                      <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-background-100">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse"></span>
                        正在播放现场原声 · 建议佩戴耳机观看
                      </p>
                    </div>
                  )}

                  <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-md bg-secondary-950/70 px-2.5 py-1 text-xs text-background-50">
                    <i className="ri-timer-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {item.duration}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                    <i className={`${category?.icon ?? "ri-movie-line"} w-3.5 h-3.5 flex items-center justify-center`}></i>
                    {categoryName}
                  </span>
                  {item.hot ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                      <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      站内热榜
                    </span>
                  ) : null}
                </div>

                <h1 className="font-heading text-xl md:text-3xl font-bold leading-snug text-foreground-950 text-balance">
                  {item.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-voice-line w-4 h-4 flex items-center justify-center"></i>
                    {item.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {formatDate(item.date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                    {formatViews(item.views)} 次播放
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-heart-3-line w-4 h-4 flex items-center justify-center"></i>
                    {formatViews(item.likes + (liked ? 1 : 0))}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setLiked((v) => !v)}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                      liked
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={`${liked ? "ri-heart-3-fill" : "ri-heart-3-line"} w-4 h-4 flex items-center justify-center`}></i>
                    {liked ? "已点赞" : "点赞"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSaved((v) => !v)}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                      saved
                        ? "bg-secondary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-secondary-400 hover:text-secondary-700"
                    }`}
                  >
                    <i className={`${saved ? "ri-bookmark-fill" : "ri-bookmark-line"} w-4 h-4 flex items-center justify-center`}></i>
                    {saved ? "已收藏" : "收藏"}
                  </button>
                  <Link
                    to={`/category/${item.category}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-layout-grid-line w-4 h-4 flex items-center justify-center"></i>
                    更多{categoryName}
                  </Link>
                </div>
              </div>

              <article className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">内容简介</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.excerpt}</p>
                <div className="mt-4 flex flex-col gap-4">
                  {item.paragraphs.map((p, index) => (
                    <p key={index} className="text-sm leading-relaxed text-foreground-600">
                      {p}
                    </p>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-300 pt-5">
                  <span className="text-xs text-foreground-500">相关标签：</span>
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>

              <section className="mt-8" aria-labelledby="related-title">
                <h2 id="related-title" className="font-heading text-lg font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
                  {related.map((rel) => (
                    <VideoCard key={rel.id} item={rel} />
                  ))}
                </div>
              </section>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-base font-bold text-foreground-950">全站热榜</h2>
                  <span className="text-xs text-foreground-500">实时更新</span>
                </div>
                <ol className="flex flex-col gap-1">
                  {hotList.map((hot, index) => (
                    <li key={hot.id}>
                      <Link
                        to={`/video/${hot.id}`}
                        className="flex cursor-pointer items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-50"
                      >
                        <span
                          className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 flex-shrink-0 rounded text-xs font-bold ${
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm leading-snug text-foreground-900 line-clamp-2">{hot.title}</span>
                          <span className="mt-1 block text-xs text-foreground-500">{formatViews(hot.views)} 播放</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>

                <div className="mt-5 border-t border-background-300 pt-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">按分类浏览</h3>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs cursor-pointer transition-colors ${
                          cat.slug === item.category
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-50 text-foreground-700 hover:bg-secondary-100"
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}